import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import airports from "../../../Data/AirportList.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiSwapLine,
  RiArrowRightLine,
  RiExchangeLine,
  RiStackLine,
  RiSearchLine,
  RiUserLine,
  RiCalendar2Line,
  RiAddLine,
  RiCloseLine,
  RiCheckLine,
  RiPlaneLine,
} from "react-icons/ri";
import styles from "./SearchFlightPanel5.module.css";

/* ─── Custom DatePicker Input ──────────────────────────────────────────── */
const DateCustomInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    className={styles.dateCustomInput}
    onClick={onClick}
    ref={ref}
  >
    {value || placeholder || "Select date"}
  </button>
));
DateCustomInput.displayName = "DateCustomInput";

/* ─── Main Component ───────────────────────────────────────────────────── */
const SearchFlightPanel5 = ({ mode, onNavigate, templateId, styleConfig, companyId }) => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#e05c2a",
    "--secondary-color": styleConfig?.secondary || "#f28a5b",
  };

  /* ── State ── */
  const [departureDate, setDepartureDate] = useState(new Date());
  const [returnDate, setReturnDate] = useState(
    new Date(new Date().setDate(new Date().getDate() + 7))
  );
  const [departureResults, setDepartureResults] = useState([]);
  const [arrivalResults, setArrivalResults] = useState([]);
  const [locationFrom, setLocationFrom] = useState("HYD");
  const [locationTo, setLocationTo] = useState("BOM");
  const [displayFrom, setDisplayFrom] = useState("Hyderabad (HYD)");
  const [displayTo, setDisplayTo] = useState("Mumbai (BOM)");
  const [selectedDeparture, setSelectedDeparture] = useState(null);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [cityFrom, setCityFrom] = useState("Hyderabad");
  const [cityTo, setCityTo] = useState("Mumbai");
  const [from, setFrom] = useState("Hyderabad, India");
  const [to, setTo] = useState("Mumbai, India");
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  const [infants, setInfants] = useState(0);
  const [cabinClass, setCabinClass] = useState("ECONOMY");
  const [preferred, setPreferred] = useState("REGULAR");
  const [isDirect, setIsDirect] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDomestic, setIsDomestic] = useState("DOMESTIC");
  const [journeyType, setJourneyType] = useState("ONEWAY");
  const [showPassDropdown, setShowPassDropdown] = useState(false);

  const [multiCitySegments, setMultiCitySegments] = useState([
    { id: 0, from: "Hyderabad (HYD)", to: "Mumbai (BOM)", date: new Date() },
    {
      id: 1,
      from: "Mumbai (BOM)",
      to: "Delhi (DEL)",
      date: new Date(new Date().setDate(new Date().getDate() + 5)),
    },
  ]);

  /* Refs */
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const passRef = useRef(null);

  /* ── Validation ── */
  const validate = () => {
    const temp = { from: from !== "", to: to !== "" };
    setErrors(temp);
    return Object.values(temp).every((x) => x === true);
  };

  /* ── Airport Search Handlers ── */
  const handleDepartureAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayFrom(value);
    if (value.length > 0) {
      const lv = value.toLowerCase();
      let filtered = airports.filter((a) => a.display === true && a.airportCode.toLowerCase().includes(lv)).slice(0, 10);
      if (!filtered.length) {
        filtered = airports.filter((a) => a.airportCode.toLowerCase().includes(lv) || a.cityName.toLowerCase().includes(lv) || a.airportName.toLowerCase().includes(lv)).slice(0, 10);
      }
      setDepartureResults(filtered);
    } else setDepartureResults([]);
  };

  const handleDepartureAirportClick = () => {
    setArrivalResults([]);
    const filtered = airports.filter((a) => a.display === true).slice(0, 10);
    setDepartureResults(filtered);
    fromInputRef.current?.select();
  };

  const handleDepartureSelect = (data) => {
    if (selectedArrival && selectedArrival.airportCode === data.airportCode) {
      alert("Departure and arrival airports cannot be the same.");
      return;
    }
    setDisplayFrom(`${data.cityName} (${data.airportCode})`);
    setLocationFrom(data.airportCode);
    setCityFrom(data.cityName);
    setFrom(data.cityName);
    setSelectedDeparture(data);
    setDepartureResults([]);
    if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
  };

  const handleArrivalAirportSearch = (e) => {
    const value = e.target.value;
    setDisplayTo(value);
    if (value.length > 0) {
      const lv = value.toLowerCase();
      let filtered = airports.filter((a) => a.airportCode.toLowerCase().includes(lv) || a.cityName.toLowerCase().includes(lv) || a.airportName.toLowerCase().includes(lv)).slice(0, 10);
      setArrivalResults(filtered);
    } else setArrivalResults([]);
  };

  const handleArrivalAirportClick = () => {
    setDepartureResults([]);
    const filtered = airports.filter((a) => a.display === true).slice(0, 10);
    setArrivalResults(filtered);
    toInputRef.current?.select();
  };

  const handleArrivalSelect = (data) => {
    if (selectedDeparture && selectedDeparture.airportCode === data.airportCode) {
      alert("Arrival and departure airports cannot be the same.");
      return;
    }
    setDisplayTo(`${data.cityName} (${data.airportCode})`);
    setLocationTo(data.airportCode);
    setCityTo(data.cityName);
    setTo(data.cityName);
    setSelectedArrival(data);
    setArrivalResults([]);
    if (data.countryCode !== "IN") setIsDomestic("INTERNATIONAL");
  };

  /* ── Swap ── */
  const handleSwap = () => {
    setDisplayFrom(displayTo); setDisplayTo(displayFrom);
    setLocationFrom(locationTo); setLocationTo(locationFrom);
    setSelectedDeparture(selectedArrival); setSelectedArrival(selectedDeparture);
    setFrom(selectedArrival?.cityName || cityTo); setTo(selectedDeparture?.cityName || cityFrom);
  };

  /* ── Date Handlers ── */
  const handleDepartureDateChange = (date) => {
    setDepartureDate(date);
    if (returnDate && date && returnDate < date) setReturnDate(date);
  };

  /* ── Passenger Handlers ── */
  const handleIncreaseAdults = (e) => { e.stopPropagation(); setAdults((p) => p + 1); };
  const handleDecreaseAdults = (e) => { e.stopPropagation(); if (adults > 1) setAdults((p) => p - 1); };
  const handleIncreaseKids = (e) => { e.stopPropagation(); setKids((p) => p + 1); };
  const handleDecreaseKids = (e) => { e.stopPropagation(); if (kids > 0) setKids((p) => p - 1); };
  const handleIncreaseInfants = (e) => { e.stopPropagation(); setInfants((p) => p + 1); };
  const handleDecreaseInfants = (e) => { e.stopPropagation(); if (infants > 0) setInfants((p) => p - 1); };

  /* ── Multi-city ── */
  const addSegment = () => {
    const last = multiCitySegments[multiCitySegments.length - 1];
    setMultiCitySegments([...multiCitySegments, {
      id: multiCitySegments.length, from: last?.to || "", to: "",
      date: new Date(new Date().setDate(new Date().getDate() + multiCitySegments.length * 5)),
    }]);
  };

  const removeSegment = (id) => {
    if (multiCitySegments.length > 1) setMultiCitySegments(multiCitySegments.filter((s) => s.id !== id));
  };

  const updateSegment = (id, field, value) =>
    setMultiCitySegments(multiCitySegments.map((s) => (s.id === id ? { ...s, [field]: value } : s)));

  /* ── GetFlightDestinations ── */
  function GetFlightDestinations() {
    if (journeyType === "MULTI") {
      return multiCitySegments.map((seg) => {
        const fromMatch = seg.from.match(/\(([^)]+)\)/);
        const toMatch = seg.to.match(/\(([^)]+)\)/);
        return {
          from: fromMatch ? fromMatch[1] : "HYD", to: toMatch ? toMatch[1] : "BOM",
          departureDate: moment(seg.date).format("YYYY-MM-DD"), returnDate: null,
        };
      });
    }
    return [{
      from: locationFrom, to: locationTo,
      departureDate: moment(departureDate).format("YYYY-MM-DD"),
      returnDate: journeyType === "RETURN" ? moment(returnDate).format("YYYY-MM-DD") : null,
    }];
  }

  /* ── Search ── */
  const handleSearch = (e) => {
    e.preventDefault();
    if (validate()) {
      const finalCompanyId = (mode === "builder" || mode === "preview")
        ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
        : companyId;

      const searchQuery = {
        journeyType, isDomestic, adults, kids, infants, cabinClass,
        companyId: finalCompanyId,
        preferred, isDirectFlight: isDirect, isConnectingFlight: isConnecting,
        flightTravelerPoints: GetFlightDestinations(),
      };

      if (mode === "builder") {
        onNavigate({ name: "flight-results", params: { searchQuery } });
      } else if (mode === "preview") {
        navigate(`/template-preview/${templateId}/flight-results`, { state: { searchQuery } });
      } else {
        navigate(`/template/flights2/${adults}/${kids}/${infants}/${isDomestic}`, { state: { searchQuery } });
      }
    }
  };

  /* ── Passenger display ── */
  const passengerDisplay = () => {
    const parts = [`${adults} Adult${adults !== 1 ? "s" : ""}`];
    if (kids > 0) parts.push(`${kids} Kid${kids !== 1 ? "s" : ""}`);
    if (infants > 0) parts.push(`${infants} Infant${infants !== 1 ? "s" : ""}`);
    const classMap = { ECONOMY: "Economy", BUSINESS: "Business", FIRST: "First", PREMIUM_ECONOMY: "Premium Eco" };
    return `${parts.join(", ")} · ${classMap[cabinClass] || cabinClass}`;
  };

  /* ── Click outside to close dropdowns ── */
  useEffect(() => {
    const handler = (e) => {
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(e.target) && fromInputRef.current && !fromInputRef.current.contains(e.target)) setDepartureResults([]);
      if (toDropdownRef.current && !toDropdownRef.current.contains(e.target) && toInputRef.current && !toInputRef.current.contains(e.target)) setArrivalResults([]);
      if (passRef.current && !passRef.current.contains(e.target)) setShowPassDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (loading) return <p style={{ color: "#555" }}>Loading...</p>;
  if (!user) { navigate("/"); return null; }

  /* ─── Trip type config ─── */
  const TRIP_TYPES = [
    { value: "ONEWAY", label: "One Way", icon: <RiArrowRightLine /> },
    { value: "RETURN", label: "Round Trip", icon: <RiExchangeLine /> },
    { value: "MULTI", label: "Multi City", icon: <RiStackLine /> },
  ];

  const CABIN_CLASSES = [
    { value: "ECONOMY", label: "Economy" },
    { value: "PREMIUM_ECONOMY", label: "Prem. Economy" },
    { value: "BUSINESS", label: "Business" },
    { value: "FIRST", label: "First Class" },
  ];

  const PREF_OPTIONS = [
    { value: "REGULAR", label: "Regular" },
    { value: "STUDENT", label: "Student" },
    { value: "CORPORATE", label: "Corporate" },
    { value: "SENIOR_CITIZEN", label: "Senior Citizen" },
  ];

  /* ─── Airport Field ─── */
  const AirportField = ({ label, icon, value, onChange, onFocus, results, onSelect, inputRef, dropdownRef, placeholder, fieldClass }) => (
    <div className={`${styles.field} ${fieldClass || ""}`}>
      <div className={styles.fieldLabel}>{icon} {label}</div>
      <input ref={inputRef} className={styles.fieldInput} type="text" value={value} onChange={onChange} onFocus={onFocus} onClick={onFocus} placeholder={placeholder} autoComplete="off" />
      {results.length > 0 && (
        <ul className={styles.dropdown} ref={dropdownRef}>
          {results.map((airport) => (
            <li key={airport.airportCode} className={styles.dropdownItem} onMouseDown={(e) => { e.preventDefault(); onSelect(airport); }}>
              <div className={styles.dropdownItemCode}>{airport.airportCode}</div>
              <div className={styles.dropdownItemInfo}>
                <div className={styles.dropdownItemCity}>{airport.cityName}</div>
                <div className={styles.dropdownItemAirport}>{airport.airportName}, {airport.countryName}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  /* ─── Date Field ─── */
  const DateField = ({ label, selected, onChange, minDate }) => (
    <div className={styles.field}>
      <div className={styles.fieldLabel}><RiCalendar2Line className={styles.fieldIcon} /> {label}</div>
      <DatePicker selected={selected} onChange={onChange} customInput={<DateCustomInput />} dateFormat="dd MMM yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={minDate || new Date()} yearDropdownItemNumber={50} scrollableYearDropdown wrapperClassName="sf5-datepicker" calendarClassName="sf5-datepicker" popperClassName="sf5-datepicker" />
    </div>
  );

  /* ─── Passenger Dropdown ─── */
  const PassengerField = () => (
    <div className={`${styles.field} ${styles.passField}`} ref={passRef}>
      <div className={styles.fieldLabel}><RiUserLine className={styles.fieldIcon} /> Travellers & Class</div>
      <div className={styles.fieldInput} style={{ cursor: "pointer" }} onClick={() => setShowPassDropdown((p) => !p)}>
        {passengerDisplay()}
      </div>

      {showPassDropdown && (
        <div className={styles.passDropdown}>
          <div className={styles.passDropdownTitle}>Passengers</div>

          {[
            { label: "Adults", sub: "12+ yrs", val: adults, inc: handleIncreaseAdults, dec: handleDecreaseAdults },
            { label: "Children", sub: "2–11 yrs", val: kids, inc: handleIncreaseKids, dec: handleDecreaseKids },
            { label: "Infants", sub: "0–2 yrs", val: infants, inc: handleIncreaseInfants, dec: handleDecreaseInfants },
          ].map((p) => (
            <div className={styles.passRow} key={p.label}>
              <div className={styles.passRowLeft}>
                <div className={styles.passRowLabel}>{p.label}</div>
                <div className={styles.passRowSub}>{p.sub}</div>
              </div>
              <div className={styles.passStepper}>
                <button className={styles.stepBtn} type="button" onClick={p.dec}>−</button>
                <span className={styles.stepVal}>{p.val}</span>
                <button className={styles.stepBtn} type="button" onClick={p.inc}>+</button>
              </div>
            </div>
          ))}

          <div className={styles.classDivider} />
          <div className={styles.classTitle}>Cabin Class</div>
          <div className={styles.classGrid}>
            {CABIN_CLASSES.map((c) => (
              <button key={c.value} type="button" className={`${styles.classOption} ${cabinClass === c.value ? styles.classOptionActive : ""}`} onClick={() => setCabinClass(c.value)}>{c.label}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  /* ─── RENDER ─── */
  return (
    <div className={styles.wrapper} style={themeVariables}>
      {/* Decorative background blobs */}
      <div className={styles.blobTopRight} />
      <div className={styles.blobBottomLeft} />

      <div className={styles.card}>

        {/* ── Trip Type Tabs ── */}
        <div className={styles.tripTypeRow}>
          {TRIP_TYPES.map((t) => (
            <button key={t.value} type="button" className={`${styles.tripTab} ${journeyType === t.value ? styles.tripTabActive : ""}`} onClick={() => setJourneyType(t.value)}>
              <span className={styles.tripTabIcon}>{t.icon}</span>{t.label}
            </button>
          ))}
        </div>

        {/* ── ONE WAY / RETURN ── */}
        {journeyType !== "MULTI" && (
          <div className={styles.fieldsRow}>
            <AirportField label="From" icon={<RiFlightTakeoffLine className={styles.fieldIcon} />} value={displayFrom} onChange={handleDepartureAirportSearch} onFocus={handleDepartureAirportClick} results={departureResults} onSelect={handleDepartureSelect} inputRef={fromInputRef} dropdownRef={fromDropdownRef} placeholder="City or airport" />

            <div className={styles.swapField}>
              <button className={styles.swapBtn} type="button" onClick={handleSwap} title="Swap airports"><RiSwapLine /></button>
            </div>

            <AirportField label="To" icon={<RiFlightLandLine className={styles.fieldIcon} />} value={displayTo} onChange={handleArrivalAirportSearch} onFocus={handleArrivalAirportClick} results={arrivalResults} onSelect={handleArrivalSelect} inputRef={toInputRef} dropdownRef={toDropdownRef} placeholder="City or airport" />

            <DateField label="Departure" selected={departureDate} onChange={handleDepartureDateChange} minDate={new Date()} />

            {journeyType === "RETURN" && (
              <DateField label="Return" selected={returnDate} onChange={setReturnDate} minDate={departureDate} />
            )}

            <PassengerField />

            <div className={styles.searchField}>
              <button className={styles.searchBtn} type="button" onClick={handleSearch}>
                <RiSearchLine className={styles.searchBtnIcon} /> Search Flights
              </button>
            </div>
          </div>
        )}

        {/* ── MULTI CITY ── */}
        {journeyType === "MULTI" && (
          <>
            <div className={styles.multiContainer}>
              {multiCitySegments.map((seg, idx) => (
                <div className={styles.multiSegment} key={seg.id}>
                  <div className={styles.multiSegmentNum}>LEG {idx + 1}</div>
                  <div className={styles.multiFields}>
                    <div className={styles.field} style={{ borderRight: "1px solid #e8e0d8" }}>
                      <div className={styles.fieldLabel}><RiFlightTakeoffLine className={styles.fieldIcon} /> From</div>
                      <input className={styles.fieldInput} type="text" value={seg.from} onChange={(e) => updateSegment(seg.id, "from", e.target.value)} placeholder="City or airport" />
                    </div>
                    <div className={styles.field} style={{ borderRight: "1px solid #e8e0d8" }}>
                      <div className={styles.fieldLabel}><RiFlightLandLine className={styles.fieldIcon} /> To</div>
                      <input className={styles.fieldInput} type="text" value={seg.to} onChange={(e) => updateSegment(seg.id, "to", e.target.value)} placeholder="City or airport" />
                    </div>
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}><RiCalendar2Line className={styles.fieldIcon} /> Departure</div>
                      <DatePicker selected={seg.date} onChange={(d) => updateSegment(seg.id, "date", d)} customInput={<DateCustomInput />} dateFormat="dd MMM yyyy" showYearDropdown showMonthDropdown dropdownMode="select" minDate={new Date()} wrapperClassName="sf5-datepicker" calendarClassName="sf5-datepicker" />
                    </div>
                  </div>
                  <div className={styles.multiRemove}>
                    <button className={styles.removeBtn} type="button" onClick={() => removeSegment(seg.id)} disabled={idx === 0} title="Remove leg"><RiCloseLine /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.multiBottomRow}>
              <button className={styles.addSegBtn} type="button" onClick={addSegment}><RiAddLine /> Add another flight</button>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-end" }}>
                <PassengerField />
                <div className={styles.searchField}>
                  <button className={styles.searchBtn} type="button" onClick={handleSearch}>
                    <RiSearchLine className={styles.searchBtnIcon} /> Search Flights
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Preferences Row ── */}
        <div className={styles.prefRow}>
          <span className={styles.prefLabel}>Fare Type</span>
          {PREF_OPTIONS.map((p) => (
            <button key={p.value} type="button" className={`${styles.prefChip} ${preferred === p.value ? styles.prefChipActive : ""}`} onClick={() => setPreferred(p.value)}>
              {preferred === p.value && <span className={styles.prefChipDot} />} {p.label}
            </button>
          ))}

          <div style={{ flex: 1 }} />

          <label className={`${styles.checkChip} ${isDirect ? styles.checkChipActive : ""}`}>
            <input type="checkbox" checked={isDirect} onChange={(e) => setIsDirect(e.target.checked)} />
            {isDirect ? <RiCheckLine className={styles.checkIcon} /> : <RiPlaneLine className={styles.checkIcon} />} Direct Flight
          </label>

          <label className={`${styles.checkChip} ${isConnecting ? styles.checkChipActive : ""}`}>
            <input type="checkbox" checked={isConnecting} onChange={(e) => setIsConnecting(e.target.checked)} />
            {isConnecting ? <RiCheckLine className={styles.checkIcon} /> : <RiExchangeLine className={styles.checkIcon} />} Connecting
          </label>
        </div>

      </div>
    </div>
  );
};

export default SearchFlightPanel5;